import { Phone, Mail, MapPin, Clock, AlertTriangle } from "lucide-react";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  EMAIL,
  SERVICE_AREA,
} from "../lib/constants";
import { SectionLabel } from "../components/Shared";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <SectionLabel>Contact</SectionLabel>
              <h1
                data-testid="contact-headline"
                className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold tracking-tighter leading-[0.95]"
              >
                Need recovery?
                <br />
                <span className="text-brand">Contact ExpressBreakdown.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
                Tell us where you are, what vehicle you have and what has
                happened. We will confirm the best next step.
              </p>

              <div className="mt-10 space-y-px bg-white/10 border border-white/10">
                <a
                  href={PHONE_TEL}
                  data-testid="contact-phone-card"
                  className="group flex items-center gap-5 bg-black p-6 hover:bg-[#0f0f0f] transition-colors"
                >
                  <div className="h-14 w-14 flex items-center justify-center bg-brand text-black">
                    <Phone className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/50 font-bold">
                      Call
                    </div>
                    <div className="font-display font-extrabold text-2xl sm:text-3xl mt-1 group-hover:text-brand transition-colors">
                      {PHONE_DISPLAY}
                    </div>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-card"
                  className="group flex items-center gap-5 bg-black p-6 hover:bg-[#0f0f0f] transition-colors"
                >
                  <div className="h-14 w-14 flex items-center justify-center border border-brand text-brand">
                    <Mail className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/50 font-bold">
                      Email
                    </div>
                    <div className="font-display font-bold text-lg sm:text-xl mt-1 group-hover:text-brand transition-colors">
                      {EMAIL}
                    </div>
                  </div>
                </a>
                <div
                  data-testid="contact-location-card"
                  className="flex items-center gap-5 bg-black p-6"
                >
                  <div className="h-14 w-14 flex items-center justify-center border border-brand text-brand">
                    <MapPin className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/50 font-bold">
                      Service Area
                    </div>
                    <div className="font-display font-bold text-lg sm:text-xl mt-1">
                      {SERVICE_AREA}
                    </div>
                  </div>
                </div>
                <div
                  data-testid="contact-hours-card"
                  className="flex items-center gap-5 bg-black p-6"
                >
                  <div className="h-14 w-14 flex items-center justify-center border border-brand text-brand">
                    <Clock className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/50 font-bold">
                      Hours
                    </div>
                    <div className="font-display font-bold text-lg sm:text-xl mt-1">
                      24 / 7 Emergency Line
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div
                data-testid="request-recovery-card"
                className="border-2 border-brand bg-black p-8 sm:p-10"
              >
                <div className="flex items-center gap-3 text-brand">
                  <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
                  <span className="text-xs uppercase tracking-[0.28em] font-bold">
                    Request Recovery
                  </span>
                </div>
                <h2 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold tracking-tight">
                  Calling is fastest.
                </h2>
                <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed">
                  When you're broken down, every minute matters. Skip forms —
                  call us directly and we'll assess the issue, confirm
                  location, and dispatch the right recovery quickly.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-white/80">
                  <li className="flex gap-3">
                    <span className="text-brand font-bold">01.</span>
                    Have your location ready (postcode, road, landmarks).
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand font-bold">02.</span>
                    Know your vehicle type (car, van, LCV).
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand font-bold">03.</span>
                    Describe what happened.
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand font-bold">04.</span>
                    Tell us where you'd like the vehicle taken.
                  </li>
                </ul>
                <a
                  href={PHONE_TEL}
                  data-testid="request-recovery-btn"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-brand text-black font-bold uppercase tracking-wider text-base px-7 py-5 btn-brutalist"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.75} />
                  Request Recovery · {PHONE_DISPLAY}
                </a>
                <p className="mt-4 text-xs text-white/50 text-center">
                  Always move to a safe location before calling where possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold tracking-tight">
            One call. Fast response.
          </h2>
          <a
            href={PHONE_TEL}
            data-testid="contact-final-call"
            className="inline-flex items-center gap-3 bg-black text-brand font-bold uppercase tracking-wider px-7 py-5"
          >
            <Phone className="h-5 w-5" />
            Call {PHONE_DISPLAY}
          </a>
        </div>
      </section>
    </div>
  );
}
